import PendingIcon from "@material-ui/icons/CachedOutlined"
import PassedIcon from "@material-ui/icons/CheckCircleOutline"
import { makeStyles } from "@material-ui/styles"
import ArchiveIcon from "@mui/icons-material/Archive"
import NotInterestedIcon from "@mui/icons-material/NotInterested"
import ReplayIcon from "@mui/icons-material/Replay"
import StarIcon from "@mui/icons-material/Star"
import VisibilityIcon from "@mui/icons-material/Visibility"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import Text from "@mui/material/Typography"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  createInsightCache,
  createInsightUpdate,
  fetchInsightCache,
  fetchUserInsights,
} from "../../actions/userInsightsActions"
import { CACHE_STATUS, FEATURES } from "../../util/constants"

const useStyles = makeStyles({
  spacing: {
    marginRight: "10px",
  },
  icons: {
    color: "#6A6A6A",
    alignItems: "center",
    width: "100%",
  },
  hover: {
    "&:hover": {
      color: "#9F9F9F",
    },
  },
})

export default function BulkUpdatesToolbar(props) {
  const {
    bulkUpdate,
    setBulkUpdate,
    setSelectedRows,
    selectedRows,
    onActionClick,
    actions,
    canUndo,
    canRedo,
    onUndoClick,
    onRedoClick,
    setSelectAll,
  } = props

  const [cacheStatus, setCacheStatus] = useState(CACHE_STATUS.GOOD)
  const [cacheTime, setCacheTime] = useState(0)

  const userCacheData = useSelector((state) => state.userInsights.userInsightCache)
  const lastInsightFetch = useSelector((state) => state.userInsights.lastInsightFetch)
  const features = useSelector((state) => state.feature.features)
  const user = useSelector((state) => state.user)

  const dispatch = useDispatch()
  const classes = useStyles()

  useEffect(() => {
    dispatch(fetchInsightCache())
    const interval = setInterval(() => dispatch(fetchInsightCache()), 5000)
    return () => clearInterval(interval)
  }, [dispatch])

  useEffect(() => {
    let newStatus
    let newTime
    if (
      features &&
      features.filter((feature) => feature.key === FEATURES.COLLECTIONS && feature.enabled === true)
        .length > 0
    ) {
      newStatus = userCacheData.collection_cache_status
      newTime = userCacheData.collection_seconds
    } else {
      newStatus = userCacheData.insight_cache_status
      newTime = userCacheData.insight_seconds
    }

    setCacheStatus(newStatus)
    setCacheTime(newTime)

    if (newStatus === CACHE_STATUS.GOOD && lastInsightFetch <= Date.now() - newTime * 1000) {
      dispatch(fetchUserInsights())
    }
  }, [userCacheData, features, lastInsightFetch, dispatch])

  const handleClick = async () => {
    if (user?.userIdSql && user.userIdSql !== 131) {
      await createInsightUpdate(bulkUpdate)
      setBulkUpdate([])
      dispatch(fetchInsightCache())
    }
  }

  const handleRefreshClick = async () => {
    await createInsightCache()
  }

  const handleClickAcknowledge = async (event) => {
    const filteredRows = selectedRows.filter((row) => actions[row] !== "acknowledge")
    onActionClick(event, filteredRows, "acknowledge")
    setSelectedRows([])
    setSelectAll(false)
  }

  const handleClickArchive = async (event) => {
    const filteredRows = selectedRows.filter((row) => actions[row] !== "archive")
    onActionClick(event, filteredRows, "archive")
    setSelectedRows([])
    setSelectAll(false)
  }

  const handleClickFavorite = async (event) => {
    const filteredRows = selectedRows.filter((row) => actions[row] !== "favorite")
    onActionClick(event, filteredRows, "favorite")
    setSelectedRows([])
    setSelectAll(false)
  }

  const handleClickRemove = async (event) => {
    const filteredRows = selectedRows.filter((row) => actions[row] !== null)
    onActionClick(event, filteredRows, null)
    setSelectedRows([])
    setSelectAll(false)
  }

  return (
    <>
      <Box>
        <Box className={classes.icons}>
          <Tooltip title="Acknowledge all selected rows">
            <IconButton
              disabled={selectedRows.length < 1}
              onClick={(event) => handleClickAcknowledge(event)}
            >
              <VisibilityIcon className={classes.hover} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Archive all selected rows">
            <IconButton
              disabled={selectedRows.length < 1}
              onClick={(event) => handleClickArchive(event)}
            >
              <ArchiveIcon className={classes.hover} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Favorite all selected rows">
            <IconButton
              disabled={selectedRows.length < 1}
              onClick={(event) => handleClickFavorite(event)}
            >
              <StarIcon className={classes.hover} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear all selected rows">
            <IconButton
              disabled={selectedRows.length < 1}
              onClick={(event) => handleClickRemove(event)}
            >
              <NotInterestedIcon className={classes.hover} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <div className={classes.spacing} />
      <Text>
        {selectedRows.length > 1
          ? `${selectedRows.length} rows selected`
          : selectedRows.length === 1
            ? `${selectedRows.length} rows selected`
            : ""}
      </Text>
      <div style={{ flex: 1 }} />
      {cacheStatus === CACHE_STATUS.GOOD ? (
        <Tooltip title={"Cache up to date"}>
          <PassedIcon style={{ color: "green" }} />
        </Tooltip>
      ) : (
        <Tooltip title={"Cache stale - updating..."}>
          <PendingIcon style={{ color: "#ebc934" }} />
        </Tooltip>
      )}
      <Tooltip title={"Manually refresh the insight cache"}>
        <Button
          disabled={cacheStatus === CACHE_STATUS.STALE && cacheTime < 300}
          variant="outlined"
          color="primary"
          onClick={handleRefreshClick}
          style={{ marginLeft: 10 }}
        >
          Refresh Cache
        </Button>
      </Tooltip>
      <Tooltip title="Undo Action">
        <IconButton disabled={!canUndo} onClick={onUndoClick}>
          <ReplayIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Redo Action">
        <IconButton disabled={!canRedo} onClick={onRedoClick}>
          <ReplayIcon style={{ transform: "scaleX(-1)" }} />
        </IconButton>
      </Tooltip>
      <div className={classes.spacing} />
      <Button
        disabled={bulkUpdate.length === 0 || cacheStatus !== CACHE_STATUS.GOOD}
        variant="outlined"
        color="primary"
        onClick={handleClick}
      >
        Save Actions
      </Button>
    </>
  )
}
